/**
 * @class HttpClient
 * @description A lightweight, type-safe HTTP client that uses the native fetch API.
 */
export class HttpClient {
  private baseURL: string;
  private headers: HeadersInit;

  /**
   * @constructor
   * @param {string} baseURL - The base URL for all requests.
   * @param {HeadersInit} [headers={}] - Default headers to be sent with every request.
   */
  constructor(baseURL: string, headers: HeadersInit = {}) {
    this.baseURL = baseURL;
    this.headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json, text/plain, */*',
      ...headers,
    };
  }

  /**
   * @method setHeaders
   * @description Sets or overrides the default headers.
   * @param {HeadersInit} headers - The headers to set.
   */
  public setHeaders(headers: HeadersInit): void {
    this.headers = { ...this.headers, ...headers };
  }

  /**
   * @private
   * @method createSafeSearchParams
   * @description Creates a URLSearchParams object, filtering out null/undefined values.
   * @param {Record<string, any>} params - The parameters object.
   * @returns {URLSearchParams} A clean URLSearchParams instance.
   */
  private createSafeSearchParams(params: Record<string, any>): URLSearchParams {
    const filteredEntries = Object.entries(params).filter(([_, value]) => {
      return value !== null && value !== undefined;
    });
    return new URLSearchParams(Object.fromEntries(filteredEntries));
  }


  /**
   * @private
   * @method request
   * @description A generic request method to handle all HTTP verbs.
   * @param {string} endpoint - The API endpoint to call.
   * @param {RequestInit & { params?: Record<string, any> }} options - The options for the fetch request, including optional params.
   * @returns {Promise<T>} - A promise that resolves to the response data.
   */
  private async request<T>(endpoint: string, options: RequestInit & { params?: Record<string, any> }): Promise<T> {
    let url = `${this.baseURL}${endpoint}`;

    if (options.params) {
      const searchParams = this.createSafeSearchParams(options.params);
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    const combinedHeaders = {
      ...this.headers,
      ...options.headers,
    };

    const cleanHeaders = Object.fromEntries(
      Object.entries(combinedHeaders).filter(([, value]) => value !== null && value !== undefined)
    ) as HeadersInit;

    const config: RequestInit = {
      ...options,
      headers: cleanHeaders,
    };

    try {
      const response = await fetch(url, config);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        const text = await response.text();
        return text ? JSON.parse(text) : ({} as T);
      }
      return (await response.text()) as any;
    } catch (error) {
      throw error;
    }
  }

  /**
   * @method get
   * @description Sends a GET request.
   * @param {string} endpoint - The API endpoint.
   * @param {RequestInit} [options={}] - Optional fetch request options.
   * @returns {Promise<T>} - A promise that resolves to the response data.
   */
  public get<T>(endpoint: string, options: RequestInit & {params?: Record<string, any>} = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  /**
   * @method post
   * @description Sends a POST request.
   * @param {string} endpoint - The API endpoint.
   * @param {any} [body] - The request body.
   * @param {RequestInit} [options={}] - Optional fetch request options.
   * @returns {Promise<T>} - A promise that resolves to the response data.
   */
  public post<T>(endpoint: string, body?: any, options: RequestInit & {params?: Record<string, any>} = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : null,
    });
  }

  /**
   * @method put
   * @description Sends a PUT request.
   * @param {string} endpoint - The API endpoint.
   * @param {any} [body] - The request body.
   * @param {RequestInit} [options={}] - Optional fetch request options.
   * @returns {Promise<T>} - A promise that resolves to the response data.
   */
  public put<T>(endpoint: string, body?: any, options: RequestInit & {params?: Record<string, any>} = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : null,
    });
  }

  /**
   * @method delete
   * @description Sends a DELETE request.
   * @param {string} endpoint - The API endpoint.
   * @param {RequestInit} [options={}] - Optional fetch request options.
   * @returns {Promise<T>} - A promise that resolves to the response data.
   */
  public delete<T>(endpoint: string, options: RequestInit & {params?: Record<string, any>} = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

/**
 * @function isCancel
 * @description Checks if an error is a request cancellation error.
 * @param {any} error - The error to check.
 * @returns {boolean} - True if the error is a cancellation error.
 */
export function isCancel(error: any): boolean {
  return error instanceof DOMException && error?.name === 'AbortError';
}

export const apiClient = new HttpClient(import.meta.env.VITE_API_BASE_URL);
export const httpClient = new HttpClient('');
